import { useLayoutEffect, useRef, useEffect, useCallback, useMemo, useState } from 'react';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  DataZoomSliderComponent,
} from 'echarts/components';
import { UniversalTransition, LabelLayout } from 'echarts/features';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  useGetCubeDataEntryChart,
  useGetCubeMemberDropdownByCube,
  useGetCubeXMemberDropdownByCube,
  useGetLayerListDropdownByCube,
  useGetCubeTimeInterval,
  useGetCubeAttributesDropdown,
} from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { useGetCubeDefinitionDetails } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { flattenTreeData, renderOptionsKtree, getListBoxPropsAutoScrolItem } from 'utils/Object';
import { prepareServerOption } from 'utils/ChartUtils';
import { CUBE_CHART_TYPE_OPTIONS } from './CubeChartDisplayButton';

echarts.use([
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  DataZoomComponent,
  DataZoomSliderComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  BarChart,
  LabelLayout,
  CanvasRenderer,
  UniversalTransition,
]);

function reviveFunctions(obj: any) {
  if (obj && typeof obj === 'object') {
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (typeof val === 'string' && val.trim().startsWith('function')) {
        // eslint-disable-next-line no-eval
        obj[key] = eval(`(${val})`);
      } else if (val && typeof val === 'object') {
        reviveFunctions(val);
      }
    }
  }
}

// ─── Line chart skeleton ─────────────────────────────────────────────────────

function LineChartSkeleton() {
  return (
    <Stack height='100%' width='100%' p={3} justifyContent='center'>
      <Skeleton variant='rectangular' animation='wave' sx={{ width: '100%', height: '70%', borderRadius: 1 }} />
      <Stack direction='row' justifyContent='space-between' mt={1}>
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Skeleton key={i} variant='text' animation='wave' sx={{ width: 40 }} />
        ))}
      </Stack>
    </Stack>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModalCubeChartDisplayProps {
  open: boolean;
  onClose: () => void;
  cubeId?: string;
  initialMemberId?: string;
  initialXMemberId?: string;
  initialLayerIds?: string[];
  initialTimeIntervalId?: string;
  initialAttributes?: AutoCompleteItem[];
  initialChartType?: string;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const filterAutoCompleteStyle = {
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function ModalCubeChartDisplay(props: ModalCubeChartDisplayProps) {
  const {
    open,
    onClose,
    cubeId,
    initialMemberId,
    initialXMemberId,
    initialLayerIds,
    initialTimeIntervalId,
    initialAttributes,
    initialChartType,
  } = props;

  // ── Local filter state (IDs / objects) ──
  const [memberId, setMemberId] = useState<string | undefined>();
  const [xMemberId, setXMemberId] = useState<string | undefined>();
  const [layerIds, setLayerIds] = useState<string[]>([]);
  const [timeIntervalId, setTimeIntervalId] = useState<string | undefined>();
  const [selectedAttributes, setSelectedAttributes] = useState<AutoCompleteItem[]>([]);
  const [chartType, setChartType] = useState<string>('LINE_CHART');

  // ── Initialize when modal opens ──
  useEffect(() => {
    if (open) {
      setMemberId(initialMemberId);
      setXMemberId(initialXMemberId);
      setLayerIds(initialLayerIds || []);
      setTimeIntervalId(initialTimeIntervalId);
      setSelectedAttributes(initialAttributes || []);
      setChartType(initialChartType || 'LINE_CHART');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Dropdown data hooks ──
  const { data: cubeDefinitionDetails } = useGetCubeDefinitionDetails(cubeId);
  const { data: memberDropdownData, isLoading: isFetchingMember } = useGetCubeMemberDropdownByCube(cubeId);
  const { data: memberXDropdownData } = useGetCubeXMemberDropdownByCube(cubeId);
  const { data: layerDropdownData, isLoading: isFetchingLayer } = useGetLayerListDropdownByCube(cubeId);
  const { data: timeFrameDropdownData, isLoading: isFetchingTimeFrame } = useGetCubeTimeInterval(cubeId);
  const { data: attributeDropdownData } = useGetCubeAttributesDropdown(cubeId, memberId);

  // ── Derived dropdown options ──
  const memberOptions: DropdownItem[] = useMemo(() => {
    if (memberDropdownData) return flattenTreeData(memberDropdownData);
    return [];
  }, [memberDropdownData]);

  const memberXOptions: DropdownItem[] = useMemo(() => {
    if (memberXDropdownData) return flattenTreeData(memberXDropdownData);
    return [];
  }, [memberXDropdownData]);

  const layerOptions: DropdownItem[] = useMemo(() => layerDropdownData?.data ?? [], [layerDropdownData?.data]);
  const timeIntervalOptions: DropdownItem[] = useMemo(
    () => timeFrameDropdownData?.data ?? [],
    [timeFrameDropdownData?.data],
  );
  const attributeOptions: AutoCompleteItem[] = useMemo(
    () => attributeDropdownData?.data ?? [],
    [attributeDropdownData?.data],
  );

  const lowestMemberLevel = useMemo(() => {
    // @ts-ignore
    return memberOptions.reduce((acc, item) => (item.level > acc ? item.level : acc), 0);
  }, [memberOptions]);

  const lowestMemberXLevel = useMemo(() => {
    // @ts-ignore
    return memberXOptions.reduce((acc, item) => (item.level > acc ? item.level : acc), 0);
  }, [memberXOptions]);

  const hasXDimension = !!cubeDefinitionDetails?.data?.subjectX;

  // ── Resolve selected dropdown items from IDs ──
  const selectedMember = useMemo(() => {
    if (!memberId) return null;
    return memberOptions.find((m: DropdownItem) => m.value === memberId) || null;
  }, [memberOptions, memberId]);

  const selectedXMember = useMemo(() => {
    if (!xMemberId) return null;
    return memberXOptions.find((m: DropdownItem) => m.value === xMemberId) || null;
  }, [memberXOptions, xMemberId]);

  const selectedLayers = useMemo(() => {
    if (layerIds.length === 0) return [];
    return layerOptions.filter((l: DropdownItem) => layerIds.includes(l.value));
  }, [layerOptions, layerIds]);

  const selectedTimeInterval = useMemo(() => {
    if (!timeIntervalId) return null;
    return timeIntervalOptions.find((t: DropdownItem) => t.value === timeIntervalId) || null;
  }, [timeIntervalOptions, timeIntervalId]);

  // ── Chart data ──
  const attributeIds = useMemo(() => selectedAttributes.map((a) => a.value?.toString()), [selectedAttributes]);

  const { data: chartResponse, isLoading } = useGetCubeDataEntryChart(
    cubeId,
    memberId,
    layerIds,
    attributeIds,
    xMemberId,
    timeIntervalId,
    chartType,
    { enabled: open },
  );

  // ── ECharts rendering ──
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  const initializeChart = useCallback(() => {
    const chartViewData = chartResponse?.data?.chartViewData;
    if (chartViewData && chartRef.current) {
      const { clientWidth, clientHeight } = chartRef.current;
      if (clientWidth > 0 && clientHeight > 0) {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.dispose();
        }

        const myChart = echarts.init(chartRef.current);
        chartInstanceRef.current = myChart;

        const option = prepareServerOption(chartViewData, dpr);
        if (myChart && option) {
          reviveFunctions(option);
          myChart.setOption(option);
        }
      }
    }
  }, [chartResponse?.data?.chartViewData, dpr]);

  useLayoutEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        initializeChart();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [open, initializeChart]);

  useEffect(() => {
    if (!open && chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
      }
    };
  }, []);

  // ── Export chart as PNG ──
  const handleDownloadPng = () => {
    if (chartInstanceRef.current) {
      const url = chartInstanceRef.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
      const link = document.createElement('a');
      link.download = 'chart.png';
      link.href = url;
      link.click();
    }
  };

  // ── Filter change handlers ──
  const handleMemberChange = (_: any, value: DropdownItem | null) => {
    setMemberId(value?.value?.toString());
  };

  const handleXMemberChange = (_: any, value: DropdownItem | null) => {
    setXMemberId(value?.value?.toString());
  };

  const handleLayerChange = (_: any, newValue: DropdownItem[]) => {
    setLayerIds(newValue.map((l) => l.value?.toString()));
  };

  const handleTimeIntervalChange = (_: any, value: DropdownItem | null) => {
    setTimeIntervalId(value?.value?.toString());
  };

  const modalContentStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1200,
    maxWidth: '92vw',
    bgcolor: 'background.paper',
    borderRadius: 1,
    outline: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={modalContentStyle}>
        {/* ── Header ── */}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ px: 3, py: 1.5, flexShrink: 0 }}
        >
          <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797' }}>
            Chart Display
          </Typography>
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Tooltip title='Download as PNG'>
              <IconButton
                size='small'
                onClick={handleDownloadPng}
                disabled={!chartResponse?.data?.chartViewData}
              >
                <FileDownloadOutlinedIcon fontSize='small' sx={{ color: '#98A2AE' }} />
              </IconButton>
            </Tooltip>
            <IconButton size='small' onClick={onClose}>
              <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
            </IconButton>
          </Stack>
        </Stack>

        {/* ── Body ── */}
        <Stack spacing={1.5} sx={{ px: 3, py: 2, flex: 1, minHeight: 0 }}>
          {/* ── Top filter row: Panel fields ── */}
          <Stack
            direction='row'
            gap={2}
            alignItems='center'
            sx={{ bgcolor: '#FBFBFB', py: 1, px: 1.5, borderRadius: 1, border: '1px solid #E3EBF6' }}
          >
            <Autocomplete
              size='small'
              sx={{ width: 280, ...filterAutoCompleteStyle }}
              options={memberOptions}
              value={selectedMember}
              onChange={handleMemberChange}
              getOptionLabel={(option: DropdownItem) => option.label}
              renderOption={(props, option, state) => renderOptionsKtree(props, option, state, lowestMemberLevel)}
              ListboxProps={{ ...getListBoxPropsAutoScrolItem(memberOptions, selectedMember) }}
              clearIcon={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isFetchingMember ? 'Loading...' : cubeDefinitionDetails?.data?.member?.label || 'Member'}
                />
              )}
            />

            {hasXDimension && (
              <Autocomplete
                size='small'
                sx={{ width: 280, ...filterAutoCompleteStyle }}
                options={memberXOptions}
                value={selectedXMember}
                onChange={handleXMemberChange}
                getOptionLabel={(option: DropdownItem) => option.label}
                renderOption={(props, option, state) => renderOptionsKtree(props, option, state, lowestMemberXLevel)}
                ListboxProps={{ ...getListBoxPropsAutoScrolItem(memberXOptions, selectedXMember) }}
                clearIcon={null}
                renderInput={(params) => (
                  <TextField {...params} label={cubeDefinitionDetails?.data?.subjectX?.label || 'X-Member'} />
                )}
              />
            )}

            <Autocomplete
              multiple
              disableCloseOnSelect
              size='small'
              sx={{ width: 220, ...filterAutoCompleteStyle }}
              options={layerOptions}
              value={selectedLayers}
              onChange={handleLayerChange}
              getOptionLabel={(option: DropdownItem) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderTags={(value) =>
                value.length > 0 ? (
                  <Chip
                    label={value.length === 1 ? value[0].label : `${value.length} layers`}
                    size='small'
                    onDelete={() => setLayerIds([])}
                    sx={{ fontSize: '11px', height: 24, bgcolor: '#EDF2FF', color: '#3B4797', fontWeight: 600 }}
                  />
                ) : null
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ ...props.style, padding: '2px 8px' }}>
                  <Checkbox
                    checked={selected}
                    size='small'
                    sx={{ mr: 0.5, p: 0.5, color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                  />
                  <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
                </li>
              )}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label={isFetchingLayer ? 'Loading...' : 'Layer'} />}
            />

            <Autocomplete
              size='small'
              sx={{ width: 150, ...filterAutoCompleteStyle }}
              options={timeIntervalOptions}
              value={selectedTimeInterval}
              onChange={handleTimeIntervalChange}
              getOptionLabel={(option: DropdownItem) => option.label}
              clearIcon={null}
              renderInput={(params) => (
                <TextField {...params} label={isFetchingTimeFrame ? 'Loading...' : 'Time Interval'} />
              )}
            />
          </Stack>

          {/* ── Attribute filter (from CubeChartDisplayButton) ── */}
          <Stack
            direction='row'
            gap={2}
            alignItems='center'
            sx={{ bgcolor: '#FBFBFB', py: 1, px: 1.5, borderRadius: 1, border: '1px solid #E3EBF6' }}
          >
            <Autocomplete
              multiple
              disableCloseOnSelect
              size='small'
              sx={{ width: 300, ...filterAutoCompleteStyle }}
              options={attributeOptions}
              getOptionLabel={(option: AutoCompleteItem) => (option.label as string) || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={selectedAttributes}
              onChange={(_event, newValue) => {
                setSelectedAttributes(newValue);
              }}
              renderTags={(value) =>
                value.length > 0 ? (
                  <Chip
                    label={value.length === 1 ? (value[0].label as string) : `${value.length} attributes selected`}
                    size='small'
                    onDelete={() => setSelectedAttributes([])}
                    sx={{ fontSize: '11px', height: 24, bgcolor: '#EDF2FF', color: '#3B4797', fontWeight: 600 }}
                  />
                ) : null
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ ...props.style, padding: '2px 8px' }}>
                  <Checkbox
                    checked={selected}
                    size='small'
                    sx={{ mr: 0.5, p: 0.5, color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                  />
                  <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
                </li>
              )}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label='Attribute' />}
            />

            {CUBE_CHART_TYPE_OPTIONS.map((opt) => {
              const isSelected = chartType === opt.value;
              return (
                <Stack
                  key={opt.value}
                  direction='row'
                  alignItems='center'
                  gap={0.75}
                  onClick={() => setChartType(opt.value)}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    border: isSelected ? '1.5px solid #42BB93' : '1px solid #e0e3e8',
                    bgcolor: isSelected ? '#f0faf6' : '#fff',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#42BB93', bgcolor: '#f0faf6' },
                  }}
                >
                  {opt.icon}
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: isSelected ? '#2a7d63' : '#555', whiteSpace: 'nowrap' }}>
                    {opt.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>

          {/* ── Divider ── */}
          <Divider sx={{ borderColor: '#E3EBF6' }} />

          {/* ── Chart area ── */}
          <Box sx={{ width: '100%', height: 550 }}>
            {isLoading && <LineChartSkeleton />}
            {!isLoading && !chartResponse?.data?.chartViewData && (
              <Stack alignItems='center' justifyContent='center' height='100%'>
                <Typography sx={{ fontSize: 13, color: '#aaa' }}>
                  {selectedAttributes.length === 0 ? 'Select attributes to display chart' : 'No chart data available'}
                </Typography>
              </Stack>
            )}
            {!isLoading && chartResponse?.data?.chartViewData && (
              <Box ref={chartRef} sx={{ width: '100%', height: '100%' }} />
            )}
          </Box>
        </Stack>
      </Stack>
    </Modal>
  );
}
